// A sensor in the desktop room grid — the same two states the mobile card
// shows, in this shell's language.
//
// It replaces what GenericControlCard did for these devices, which was to fetch
// their outputs, find none, and print "No controllable outputs" over a grey
// icon. That is true and useless: the device has plenty to say, it just has
// nothing to switch. Here the state is the card.
//
// Nothing is fetched. A sensor has no outputs to name and no channels to read,
// so the row's own `Status` plus the live topic is the whole story — which also
// means this card costs zero requests where the generic one cost one per sensor.

import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import {
  sensorStateOf, sensorSilence, silenceText, SENSOR_ALERT, SENSOR_NORMAL,
} from '../../../core/devices/sensorState';
import { ageOf } from '../../../core/devices/deviceStatus';
import { parseServerTime } from '../../../core/devices/firmwareInfo';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';
import RfLastHeard from '../../shared/RfLastHeard';

// The RF receiver's status is the last code it heard — shown as the sensor it
// came from (RfLastHeard), never as a red disc over a string of bits.
const RF_RECEIVER_DEVICE_ID = 47;

// Fixed, not themed: a red/green pair a customer's palette could wash out would
// defeat the point of the card.
const COLOURS = {
  [SENSOR_NORMAL]: '#2E7D32',
  [SENSOR_ALERT]: '#C62828',
};
const NEUTRAL = '#9E9E9E';

export default function SensorControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;
  const [status, , liveAt] = useDeviceStatus(
    uno,
    parseStatus(device.Status || device.DeviceStatus || ''),
  );
  const { state, label } = sensorStateOf(status, device.DeviceID);
  // Silent for hours: the state is LAST KNOWN, drawn grey with its age.
  const silent = sensorSilence(device, liveAt, parseServerTime, ageOf);
  const colour = silent ? NEUTRAL : (COLOURS[state] || NEUTRAL);
  // ⚠️ A RECEIVER IS NOT THE SENSOR (owner, 2026-10-10): the sensor it heard
  // already has its own tile in the room, so the receiver card says it in ONE
  // line and stays its own height instead of stretching to the tallest card in
  // the row — it was drawn twice, the second time big.
  const receiver = Number(device.DeviceID) === RF_RECEIVER_DEVICE_ID;

  return (
    <Card sx={{
      p: 1.25,
      borderRadius: 3,
      height: receiver ? 'auto' : '100%',
      alignSelf: receiver ? 'flex-start' : undefined,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu device={device} />
      </Box>

      <Box sx={{
        flex: receiver ? undefined : 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: receiver ? 'stretch' : 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: receiver ? 0 : 0.75,
      }}
      >
        {receiver ? (
          <RfLastHeard
            receiverUno={uno}
            status={status}
            liveAt={liveAt}
            locationId={device.LocationID ?? device.LocationId ?? 0}
            compact
          />
        ) : (
          <>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: colour,
              boxShadow: `0 0 0 6px ${colour}22`,
            }}
            />
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: colour, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.2 }}
            >
              {silent ? `Last: ${label}` : label}
            </Typography>
            {silent && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                {`not heard for ${silenceText(silent)}`}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Card>
  );
}
